import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, Mic, Download, KeyRound, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Strip stage directions so the TTS reads ONLY the spoken words.
// DIRECTION labels (visual/overlay/scene/transition…) drop the whole line;
// SPOKEN labels (hook/cta/dialogue…) keep the words after the colon.
const DIRECTION_RE = /^\s*(visual[s]?|overlay|on-?screen text|text overlay|transition|scene\s*\d*|b-?roll|shot|angle|filming|caption|setting|props?|action|beat\s*\d*|step\s*\d*)\b[^:]*:/i;
const SPOKEN_LABEL_RE = /^\s*(hook|cta|call to action|dialogue|spoken|voice ?over|vo|opening line|mid-?roll|script)\b[^:]*:\s*/i;

const cleanScriptForVoiceover = (raw) => {
  if (!raw) return '';
  const out = [];
  for (let line of String(raw).split('\n')) {
    line = line.replace(/\[[^\]]*\]/g, ' ').replace(/\([^)]*\)/g, ' ');
    if (DIRECTION_RE.test(line)) continue; // non-spoken direction line — drop
    const m = line.match(SPOKEN_LABEL_RE);
    if (m) line = line.slice(m[0].length);
    line = line.replace(/\s{2,}/g, ' ').trim();
    if (!line) continue;
    if (/^[-–—•*#>\d\s:.\/]+$/.test(line)) continue; // pure timestamps / bullets / dividers
    out.push(line);
  }
  return out.join('\n').trim();
};

const PROVIDERS = [
  { key: 'elevenlabs', label: 'ElevenLabs' },
  { key: 'fish', label: 'Fish Audio' },
];

// Reusable "turn this script into a voiceover" control.
export const VoiceoverButton = ({ scriptText, testidPrefix = 'voiceover', defaultOpen = false }) => {
  const { getAuthHeader } = useAuth();
  const [open, setOpen] = useState(defaultOpen);
  const [provider, setProvider] = useState('elevenlabs');
  const [voices, setVoices] = useState([]);
  const [voiceId, setVoiceId] = useState('');
  const [loading, setLoading] = useState(false);
  const [audioUrl, setAudioUrl] = useState('');
  const [needKey, setNeedKey] = useState(null); // { provider, affiliate_url, message }
  const [downloading, setDownloading] = useState(false);
  const [showKeyInfo, setShowKeyInfo] = useState(false);
  // Affiliate sign-up links for the "Unlock more voices" modal.
  const [affiliateUrls, setAffiliateUrls] = useState({ elevenlabs: '', fish: '' });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/voiceover/providers`)
      .then((res) => {
        if (cancelled) return;
        const map = {};
        (res.data.providers || []).forEach((p) => { map[p.key] = p.affiliate_url; });
        setAffiliateUrls(map);
      })
      .catch(() => { /* keep fallbacks */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/voiceover/voices?provider=${provider}`);
        if (cancelled) return;
        const list = res.data.voices || [];
        setVoices(list);
        setVoiceId(list[0]?.id ?? '');
      } catch {
        setVoices([]);
      }
    })();
    return () => { cancelled = true; };
  }, [provider, open]);

  const generate = async () => {
    const spoken = cleanScriptForVoiceover(scriptText);
    if (!spoken) {
      toast.error('No spoken lines found to voice.');
      return;
    }
    setLoading(true);
    setAudioUrl('');
    setNeedKey(null);
    try {
      const res = await axios.post(
        `${API}/api/voiceover/generate`,
        { text: spoken, provider, voice_id: voiceId },
        { headers: getAuthHeader() }
      );
      const data = res.data;
      if (data.success) {
        setAudioUrl(`${API}${data.audio_url}`);
        toast.success('Voiceover ready.');
        if (data.remaining_free === 0) {
          toast.message('That was your last free voiceover — add your own key in Settings to keep going.');
        }
      } else if (data.reason === 'need_own_key') {
        setNeedKey(data);
      } else {
        toast.error(data.message || 'Could not generate voiceover.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not generate voiceover. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!audioUrl) return;
    setDownloading(true);
    try {
      const r = await axios.get(audioUrl, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = 'poettik-voiceover.mp3';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      toast.error('Download failed — try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (!open) {
    return (
      <Button
        variant="outline"
        size="sm"
        onClick={() => setOpen(true)}
        data-testid={`${testidPrefix}-open`}
      >
        <Mic className="w-4 h-4 mr-2" /> Create voiceover
      </Button>
    );
  }

  return (
    <div className="mt-4 p-4 rounded-xl bg-muted/30 border border-border" data-testid={`${testidPrefix}-panel`}>
      <div className="flex items-center gap-2 mb-3">
        <Mic className="w-4 h-4 text-primary" />
        <span className="text-sm font-semibold">Generate a voiceover</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Provider</label>
          <Select value={provider} onValueChange={(v) => setProvider(v)}>
            <SelectTrigger data-testid={`${testidPrefix}-provider`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROVIDERS.map((p) => (
                <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground mb-1 block">Voice</label>
          <Select value={voiceId} onValueChange={(v) => setVoiceId(v)}>
            <SelectTrigger data-testid={`${testidPrefix}-voice`}><SelectValue placeholder="Default" /></SelectTrigger>
            <SelectContent>
              {voices.map((v) => (
                <SelectItem key={v.id || 'default'} value={v.id || 'default'}>{v.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowKeyInfo(true)}
        className="text-xs text-primary hover:underline mb-3 inline-flex items-center gap-1"
        data-testid={`${testidPrefix}-more-voices`}
      >
        <Sparkles className="w-3.5 h-3.5" /> Want more voices? Use your own account
      </button>

      <div className="flex flex-wrap gap-2">
        <Button variant="premium" size="sm" onClick={generate} disabled={loading || !cleanScriptForVoiceover(scriptText)} data-testid={`${testidPrefix}-generate`}>
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mic className="w-4 h-4 mr-2" />}
          {loading ? 'Generating…' : 'Generate'}
        </Button>
        {audioUrl && (
          <Button variant="outline" size="sm" onClick={download} disabled={downloading} data-testid={`${testidPrefix}-download`}>
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download
          </Button>
        )}
        {!defaultOpen && <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>Close</Button>}
      </div>

      {audioUrl && (
        <audio className="w-full mt-3" controls src={audioUrl} data-testid={`${testidPrefix}-audio`} />
      )}

      {needKey && (
        <div className="mt-3 p-3 rounded-lg bg-primary/5 border border-primary/20 text-sm" data-testid={`${testidPrefix}-need-key`}>
          <p className="text-muted-foreground mb-2">{needKey.message}</p>
          <div className="flex flex-wrap gap-2">
            <Link to="/profile">
              <Button variant="premium" size="sm" data-testid={`${testidPrefix}-add-key`}>
                <KeyRound className="w-4 h-4 mr-2" /> Add my key in Settings
              </Button>
            </Link>
            <a href={needKey.affiliate_url} target="_blank" rel="noopener noreferrer">
              <Button variant="outline" size="sm">Get a {needKey.provider === 'elevenlabs' ? 'ElevenLabs' : 'Fish Audio'} key</Button>
            </a>
          </div>
        </div>
      )}
      <Dialog open={showKeyInfo} onOpenChange={setShowKeyInfo}>
        <DialogContent className="max-w-md" data-testid={`${testidPrefix}-key-dialog`}>
          <DialogHeader>
            <DialogTitle>Unlock more voices</DialogTitle>
            <DialogDescription>
              You get {2} free voiceovers on us. For unlimited voiceovers and the full voice library,
              use your own ElevenLabs or Fish Audio account — it&apos;s quick to set up.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <a href={affiliateUrls.elevenlabs || 'https://elevenlabs.io'} target="_blank" rel="sponsored noopener noreferrer">
                <Button variant="outline" className="w-full" data-testid={`${testidPrefix}-eleven-link`}>ElevenLabs</Button>
              </a>
              <a href={affiliateUrls.fish || 'https://fish.audio'} target="_blank" rel="sponsored noopener noreferrer">
                <Button variant="outline" className="w-full" data-testid={`${testidPrefix}-fish-link`}>Fish Audio</Button>
              </a>
            </div>
            <p className="text-[11px] text-muted-foreground -mt-1" data-testid={`${testidPrefix}-affiliate-disclosure`}>
              We may earn a commission if you sign up through these links.
            </p>
            <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal list-inside">
              <li>Create a free account on either site above.</li>
              <li>In your account dashboard, find and copy your <strong className="text-foreground">API key</strong>.</li>
              <li>Come back to Poettik and paste it into <strong className="text-foreground">Settings → Voiceover API keys</strong>.</li>
              <li>That&apos;s it — voiceovers now run on your own account with no limits.</li>
            </ol>
            <Link to="/profile" onClick={() => setShowKeyInfo(false)}>
              <Button variant="premium" className="w-full" data-testid={`${testidPrefix}-go-settings`}>
                <KeyRound className="w-4 h-4 mr-2" /> Go to Settings
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VoiceoverButton;
